/* @jsx h */
import { defineComponent, h, ref, onBeforeUnmount } from "vue";

/**
 * An <iframe> that resizes its height to fit the content it loads, by
 * measuring `document.documentElement.scrollHeight` inside the iframe once
 * it (same-origin) finishes loading. Falls back to a sensible min-height if
 * measurement fails (e.g. cross-origin).
 */
export const AutoHeightIframe = defineComponent<{
  src: string;
  minHeight?: number;
  maxHeight?: number;
}>({
  name: "AutoHeightIframe",
  props: ["src", "minHeight", "maxHeight"],
  setup(props) {
    const height = ref(props.minHeight ?? 240);
    const iframeRef = ref<HTMLIFrameElement | null>(null);
    let resizeObserver: ResizeObserver | null = null;
    let rafId: number | null = null;

    const scheduleMeasure = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        measure();
      });
    };

    const measure = () => {
      const iframe = iframeRef.value;
      if (!iframe) return;
      try {
        const doc = iframe.contentDocument;
        if (!doc) return;
        const contentHeight = Math.max(
          doc.documentElement.scrollHeight,
          doc.body?.scrollHeight ?? 0,
        );
        const min = props.minHeight ?? 240;
        const max = props.maxHeight ?? 1600;
        height.value = Math.min(Math.max(contentHeight, min), max);

        // Keep tracking size changes (charts rendering async, fonts loading, etc.)
        if (!resizeObserver && doc.body) {
          resizeObserver = new ResizeObserver(() => scheduleMeasure());
          resizeObserver.observe(doc.body);
        }
      } catch {
        // Cross-origin or not-yet-ready iframe — keep the current height.
      }
    };

    const handleLoad = () => {
      scheduleMeasure();
      // Re-measure a few times shortly after load for async-rendered content
      // (e.g. chart libraries that draw after onload).
      [100, 300, 800].forEach((delay) => setTimeout(scheduleMeasure, delay));
    };

    onBeforeUnmount(() => {
      resizeObserver?.disconnect();
      if (rafId !== null) cancelAnimationFrame(rafId);
    });

    return () => (
      <iframe
        ref={iframeRef}
        src={props.src}
        onLoad={handleLoad}
        class="w-full rounded-lg border border-base-content/10 bg-base-100"
        style={`height: ${height.value}px; transition: height 0.2s ease;`}
        sandbox="allow-scripts allow-same-origin allow-popups"
      />
    );
  },
});
